import { createPublicClient, erc20Abi, fallback, http, parseAbiItem, type Address, type Hash } from 'viem'
import { flatboardAbi } from './abi/flatboard'
import { chain, DEPLOY_BLOCK, FLATBOARD, WIF } from './config'

export const publicClient = createPublicClient({
  chain,
  // Robinhood Chain makes a block every ~0.1s, so receipts are worth checking every second.
  pollingInterval: 1_000,
  transport: fallback(chain.rpcUrls.default.http.map((url) => http(url, { retryCount: 1, timeout: 12_000 }))),
})

export interface Listing {
  id: number
  lister: Address
  createdAt: number
  lastBoostAt: number
  /** Heat as read from the chain at `readAt`. */
  heat: bigint
  burned: bigint
  boosts: number
  token: Address | null
  name: string
  url: string
  tagline: string
}

export interface Board {
  listings: Listing[]
  /** Chain time of the read, in seconds. */
  readAt: number
  totalBurned: bigint
  burners: { account: Address; burned: bigint }[]
}

export interface Burn {
  id: number
  booster: Address
  amount: bigint
  heat: bigint
  hash: Hash
  block: bigint
  timestamp: number | null
}

const PAGE = 100n
const ZERO: Address = '0x0000000000000000000000000000000000000000'
const boostedEvent = parseAbiItem('event Boosted(uint256 indexed id, address indexed booster, uint256 amount, uint256 heat)')
const board = { address: FLATBOARD as Address, abi: flatboardAbi } as const

export async function loadBoard(): Promise<Board> {
  const block = await publicClient.getBlock()
  const at = { blockNumber: block.number }
  const [count, totalBurned, burnerCount] = await Promise.all([
    publicClient.readContract({ ...board, functionName: 'listingCount', ...at }),
    publicClient.readContract({ ...board, functionName: 'totalBurned', ...at }),
    publicClient.readContract({ ...board, functionName: 'burnerCount', ...at }),
  ])

  const pages: Promise<readonly RawListing[]>[] = []
  for (let start = 0n; start < count; start += PAGE) {
    pages.push(publicClient.readContract({ ...board, functionName: 'getListings', args: [start, PAGE], ...at }))
  }
  const burnerPages: Promise<readonly [readonly Address[], readonly bigint[]]>[] = []
  for (let start = 0n; start < burnerCount; start += PAGE) {
    burnerPages.push(publicClient.readContract({ ...board, functionName: 'getBurners', args: [start, PAGE], ...at }))
  }

  const listings = (await Promise.all(pages)).flat().map(toListing)
  const burners = (await Promise.all(burnerPages))
    .flatMap(([accounts, amounts]) => accounts.map((account, i) => ({ account, burned: amounts[i] })))
    .sort((a, b) => (b.burned > a.burned ? 1 : b.burned < a.burned ? -1 : 0))

  return { listings, readAt: Number(block.timestamp), totalBurned, burners }
}

type RawListing = {
  id: bigint
  lister: Address
  createdAt: number
  lastBoostAt: number
  heat: bigint
  burned: bigint
  boosts: bigint
  token: Address
  name: string
  url: string
  tagline: string
}

const toListing = (l: RawListing): Listing => ({
  id: Number(l.id),
  lister: l.lister,
  createdAt: Number(l.createdAt),
  lastBoostAt: Number(l.lastBoostAt),
  heat: l.heat,
  burned: l.burned,
  boosts: Number(l.boosts),
  token: l.token === ZERO ? null : l.token,
  name: l.name,
  url: l.url,
  tagline: l.tagline,
})

/** The most recent burns, newest first. Reads logs in wide windows, newest window first. */
export async function loadRecentBurns(limit = 25): Promise<Burn[]> {
  const latest = await publicClient.getBlockNumber()
  const window = 4_000_000n
  const found: Burn[] = []
  for (let to = latest; to >= DEPLOY_BLOCK && found.length < limit; to -= window) {
    const from = to - window + 1n > DEPLOY_BLOCK ? to - window + 1n : DEPLOY_BLOCK
    const logs = await publicClient.getLogs({ address: FLATBOARD as Address, event: boostedEvent, fromBlock: from, toBlock: to })
    for (const log of logs.reverse()) {
      found.push({
        id: Number(log.args.id),
        booster: log.args.booster!,
        amount: log.args.amount!,
        heat: log.args.heat!,
        hash: log.transactionHash,
        block: log.blockNumber,
        timestamp: null,
      })
    }
    if (from === DEPLOY_BLOCK) break
  }
  const burns = found.slice(0, limit)
  const blocks = [...new Set(burns.map((b) => b.block))]
  const times = new Map(
    await Promise.all(blocks.map(async (n) => [n, Number((await publicClient.getBlock({ blockNumber: n })).timestamp)] as const)),
  )
  for (const b of burns) b.timestamp = times.get(b.block) ?? null
  return burns
}

export async function wifBalance(account: Address): Promise<bigint> {
  return publicClient.readContract({ address: WIF, abi: erc20Abi, functionName: 'balanceOf', args: [account] })
}

export async function wifAllowance(account: Address): Promise<bigint> {
  return publicClient.readContract({
    address: WIF,
    abi: erc20Abi,
    functionName: 'allowance',
    args: [account, FLATBOARD as Address],
  })
}

/** $WIF price in USD from DexScreener, or null if it can't be fetched. */
export async function wifPrice(): Promise<number | null> {
  try {
    const res = await fetch(`https://api.dexscreener.com/latest/dex/tokens/${WIF}`)
    const data = (await res.json()) as { pairs?: { priceUsd?: string; liquidity?: { usd?: number } }[] }
    const pair = [...(data.pairs ?? [])].sort((a, b) => (b.liquidity?.usd ?? 0) - (a.liquidity?.usd ?? 0))[0]
    const price = Number(pair?.priceUsd)
    return Number.isFinite(price) && price > 0 ? price : null
  } catch {
    return null
  }
}
